import type {
  InventoryItemDraft,
  CreateInventoryItemPayload,
  BulkCreateInventoryPayload,
  InventoryItemSummary
} from '../Types';
import { createEmptyDraft } from '../mockData';
import { formatUnitDefinition } from './UnitDefinition.utils';
import axios from 'axios';

/** NestJS validation errors may return message as string or string[] */
export const formatApiErrorMessage = (
  err: unknown,
  fallback: string
): string => {
  if (axios.isAxiosError(err)) {
    const msg = (err.response?.data as { message?: string | string[] })
      ?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.trim()) return msg;
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

/** Row with no item name — used for trailing entry row; excluded from save/API */
export const isBlankDraftRow = (item: InventoryItemDraft): boolean =>
  !item.name.trim();

/** Show inline validation once the row has a name (not the trailing blank row) */
export const shouldValidateDraftRow = (
  item: InventoryItemDraft,
  isTrailingBlank: boolean
): boolean => !isTrailingBlank && !isBlankDraftRow(item);

/** Row not yet persisted to location inventory (no stock record) */
export const isUnsavedDraftRow = (item: InventoryItemDraft): boolean =>
  !item.stockId;

const parseRequiredNonNegativeNumber = (
  raw: string,
  label: string
): { value: number | null; error: string | null } => {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { value: null, error: `${label} is required` };
  }
  const parsed = parseFloat(trimmed);
  if (Number.isNaN(parsed) || parsed < 0) {
    return { value: null, error: `${label} must be 0 or greater` };
  }
  return { value: parsed, error: null };
};

export const getInventoryItemRowErrors = (
  item: InventoryItemDraft,
  withLocalIdPrefix = true
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const key = (field: string) =>
    withLocalIdPrefix ? `${item.localId}.${field}` : field;

  if (!item.name.trim()) {
    errors[key('name')] = 'Name is required';
  }
  if (!item.unit.trim()) {
    errors[key('unit')] = 'Unit is required';
  }

  const hasMultiplier = item.unitMultiplier.trim() !== '';
  const hasBaseUnit = item.baseUnit.trim() !== '';
  if (hasMultiplier || hasBaseUnit) {
    if (hasMultiplier !== hasBaseUnit) {
      const msg = 'Enter both multiplier and base unit';
      if (!hasMultiplier) errors[key('unitMultiplier')] = msg;
      if (!hasBaseUnit) errors[key('baseUnit')] = msg;
    }
    if (hasMultiplier) {
      const mult = parseFloat(item.unitMultiplier);
      if (Number.isNaN(mult) || mult <= 0) {
        errors[key('unitMultiplier')] = 'Must be greater than 0';
      }
    }
  }

  if (!item.supplierUserId.trim()) {
    errors[key('supplierUserId')] = 'Supplier is required';
  }

  if (!item.locationId?.trim()) {
    errors[key('locationId')] = 'Location is required';
  }

  const onHand = parseRequiredNonNegativeNumber(item.quantityOnHand, 'On hand');
  if (onHand.error) {
    errors[key('quantityOnHand')] = onHand.error;
  }

  const minQty = parseRequiredNonNegativeNumber(item.minQuantity, 'Min qty');
  if (minQty.error) {
    errors[key('minQuantity')] = minQty.error;
  }

  if (item.minOrderQty.trim() !== '') {
    const moq = parseFloat(item.minOrderQty);
    if (Number.isNaN(moq) || moq < 0) {
      errors[key('minOrderQty')] = 'MOQ must be 0 or greater';
    }
  }

  return errors;
};

export const isCompleteDraftRow = (item: InventoryItemDraft): boolean =>
  !isBlankDraftRow(item) &&
  Object.keys(getInventoryItemRowErrors(item, false)).length === 0;

/** Row must be idle this long after last edit before background save */
export const AUTO_SAVE_QUIET_MS = 8_000;

export interface AutoSaveActivityState {
  lastEditedAt: Record<string, number>;
  focusedRowId: string | null;
  /** Persisted rows with unsaved local edits — kept during silent reload */
  dirtyPersistedRowIds?: string[];
  quietPeriodMs?: number;
}

export const isRowEligibleForAutoSave = (
  item: InventoryItemDraft,
  activity: AutoSaveActivityState,
  now = Date.now()
): boolean => {
  if (isBlankDraftRow(item) || !isCompleteDraftRow(item)) {
    return false;
  }

  const dirtyIds = new Set(activity.dirtyPersistedRowIds ?? []);
  if (!isUnsavedDraftRow(item) && !dirtyIds.has(item.localId)) {
    return false;
  }

  if (activity.focusedRowId === item.localId) {
    return false;
  }

  const lastEdit = activity.lastEditedAt[item.localId] ?? 0;
  const quietMs = activity.quietPeriodMs ?? AUTO_SAVE_QUIET_MS;
  return lastEdit > 0 && now - lastEdit >= quietMs;
};

export const getAutoSaveCandidates = (
  items: InventoryItemDraft[],
  activity?: AutoSaveActivityState
): InventoryItemDraft[] => {
  const dirtyIds = new Set(activity?.dirtyPersistedRowIds ?? []);
  const candidates = getSavableItems(items).filter(item => {
    if (!isCompleteDraftRow(item)) return false;
    if (isUnsavedDraftRow(item)) return true;
    return Boolean(item.stockId && dirtyIds.has(item.localId));
  });

  if (!activity) {
    return candidates.filter(isUnsavedDraftRow);
  }

  return candidates.filter(item => isRowEligibleForAutoSave(item, activity));
};

export const getSavableItems = (
  items: InventoryItemDraft[]
): InventoryItemDraft[] => items.filter(item => !isBlankDraftRow(item));

export const getCompleteSavableItems = (
  items: InventoryItemDraft[]
): InventoryItemDraft[] => getSavableItems(items).filter(isCompleteDraftRow);

/** Always keep one blank row at the end for quick data entry */
export const normalizeItemRows = (
  items: InventoryItemDraft[]
): InventoryItemDraft[] => {
  if (items.length === 0) {
    return [createEmptyDraft()];
  }

  const last = items[items.length - 1];
  if (!isBlankDraftRow(last)) {
    return [...items, createEmptyDraft()];
  }

  return items;
};

/** Keep unsaved in-progress rows; preserve local row order and edits on persisted rows */
export const mergeItemsAfterSilentReload = (
  currentItems: InventoryItemDraft[],
  serverItems: InventoryItemDraft[],
  savedLocalIds: string[] = [],
  activity?: AutoSaveActivityState
): InventoryItemDraft[] => {
  const savedSet = new Set(savedLocalIds);
  const dirtyIds = new Set(activity?.dirtyPersistedRowIds ?? []);
  const usedStockIds = new Set<string>();

  const serverByStockId = new Map(
    serverItems.filter(item => item.stockId).map(item => [item.stockId!, item])
  );
  const serverByRestaurantId = new Map(
    serverItems
      .filter(item => item.restaurantInventoryItemId)
      .map(item => [item.restaurantInventoryItemId!, item])
  );

  const matchNewlySavedDraft = (
    local: InventoryItemDraft
  ): InventoryItemDraft | null => {
    const match = serverItems.find(
      server =>
        server.stockId &&
        !usedStockIds.has(server.stockId) &&
        server.name.trim().toLowerCase() === local.name.trim().toLowerCase() &&
        server.unit.trim().toLowerCase() === local.unit.trim().toLowerCase()
    );
    if (match?.stockId) {
      usedStockIds.add(match.stockId);
      return match;
    }
    return null;
  };

  const ordered: InventoryItemDraft[] = [];

  for (const current of currentItems) {
    if (isBlankDraftRow(current)) continue;

    if (isUnsavedDraftRow(current) && savedSet.has(current.localId)) {
      const saved = matchNewlySavedDraft(current);
      if (saved) {
        ordered.push(saved);
        continue;
      }
    }

    if (isUnsavedDraftRow(current) && !savedSet.has(current.localId)) {
      ordered.push(current);
      continue;
    }

    if (current.stockId && serverByStockId.has(current.stockId)) {
      usedStockIds.add(current.stockId);
      ordered.push(
        dirtyIds.has(current.localId)
          ? current
          : serverByStockId.get(current.stockId)!
      );
      continue;
    }

    if (
      current.restaurantInventoryItemId &&
      serverByRestaurantId.has(current.restaurantInventoryItemId)
    ) {
      const server = serverByRestaurantId.get(
        current.restaurantInventoryItemId
      )!;
      if (server.stockId) usedStockIds.add(server.stockId);
      ordered.push(dirtyIds.has(current.localId) ? current : server);
      continue;
    }

    if (!isUnsavedDraftRow(current) && dirtyIds.has(current.localId)) {
      ordered.push(current);
    }
  }

  for (const server of serverItems) {
    if (server.stockId && !usedStockIds.has(server.stockId)) {
      ordered.push(server);
      usedStockIds.add(server.stockId);
    }
  }

  return normalizeItemRows(ordered);
};

export const validateInventoryItems = (
  items: InventoryItemDraft[]
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const savableItems = getSavableItems(items);

  if (savableItems.length === 0) {
    errors._form = 'Add at least one item';
    return errors;
  }

  savableItems.forEach(item => {
    Object.assign(errors, getInventoryItemRowErrors(item));
  });

  if (Object.keys(errors).length > 0 && !errors._form) {
    errors._form = 'Complete all required fields before saving';
  }

  return errors;
};

/** Backend still requires locationId — use branch default, not shown in UI */
export const buildBulkPayload = (
  items: InventoryItemDraft[],
  defaultLocationId: string
): BulkCreateInventoryPayload => {
  const payloadItems: CreateInventoryItemPayload[] = getSavableItems(items)
    .filter(isCompleteDraftRow)
    .map(item => {
      const entry: CreateInventoryItemPayload = {
        name: item.name.trim(),
        unit: item.unit.trim(),
        locationId: item.locationId ?? defaultLocationId
      };

      const composed = formatUnitDefinition(
        item.unit,
        item.unitMultiplier,
        item.baseUnit
      );
      if (composed) {
        entry.unitDefinition = composed;
      }
      if (item.baseUnit.trim()) {
        entry.baseUnit = item.baseUnit.trim();
      }
      const mult = parseFloat(item.unitMultiplier);
      if (!isNaN(mult) && mult > 0) {
        entry.unitMultiplier = mult;
      }

      const qty = parseFloat(item.quantityOnHand);
      if (item.quantityOnHand !== '' && !isNaN(qty) && qty >= 0) {
        entry.quantityOnHand = qty;
      }

      const minQty = parseFloat(item.minQuantity);
      if (item.minQuantity !== '' && !isNaN(minQty) && minQty >= 0) {
        entry.minQuantity = minQty;
      }

      const moq = parseFloat(item.minOrderQty);
      if (item.minOrderQty !== '' && !isNaN(moq) && moq >= 0) {
        entry.minOrderQty = moq;
      }

      if (item.note.trim()) {
        entry.note = item.note.trim();
      }

      if (item.imageUrl?.trim()) {
        entry.imageUrl = item.imageUrl.trim();
      }

      if (item.supplierUserId.trim()) {
        entry.supplierUserId = item.supplierUserId.trim();
      }

      if (item.restaurantInventoryItemId?.trim()) {
        entry.restaurantInventoryItemId = item.restaurantInventoryItemId.trim();
      }

      return entry;
    });

  return { items: payloadItems };
};

export interface RestaurantInventoryListRow {
  restaurant_inventory_item_id: string;
  stock_id: string;
  name: string;
  unit: string;
  unit_multiplier: number | null;
  base_unit: string | null;
  image_url: string | null;
  note: string | null;
  category_id?: string | null;
  category_name?: string | null;
  location_id: string | null;
  quantity_on_hand: number;
  min_quantity: number;
  min_order_qty?: number | null;
  assigned_supplier_id: string | null;
}

/**
 * Row identity for a saved inventory row. The list endpoint returns one row per
 * stock record, so an item stored in two positions would otherwise collapse to a
 * single id — making both cards share a React key and a single delete wipe both.
 */
export const buildInventoryDraftLocalId = (
  restaurantInventoryItemId: string,
  stockId?: string | null
): string =>
  stockId?.trim()
    ? `${restaurantInventoryItemId}:${stockId.trim()}`
    : restaurantInventoryItemId;

export const restaurantInventoryRowToDraft = (
  row: RestaurantInventoryListRow
): InventoryItemDraft => ({
  localId: buildInventoryDraftLocalId(
    row.restaurant_inventory_item_id,
    row.stock_id
  ),
  restaurantInventoryItemId: row.restaurant_inventory_item_id,
  stockId: row.stock_id,
  locationId: row.location_id,
  name: row.name,
  unit: row.unit,
  unitMultiplier:
    row.unit_multiplier != null ? String(row.unit_multiplier) : '',
  baseUnit: row.base_unit ?? '',
  quantityOnHand: String(row.quantity_on_hand ?? 0),
  minQuantity:
    row.min_quantity != null && row.min_quantity !== undefined
      ? String(row.min_quantity)
      : '',
  minOrderQty:
    row.min_order_qty != null &&
    row.min_order_qty !== undefined &&
    Number(row.min_order_qty) > 0
      ? String(row.min_order_qty)
      : '',
  // Prefer category name for display; fall back to free-text note.
  note: row.category_name?.trim() || row.note || '',
  imagePreview: row.image_url,
  imageName: row.image_url ? (row.image_url.split('/').pop() ?? null) : null,
  imageFile: null,
  imageUrl: row.image_url,
  supplierUserId: row.assigned_supplier_id ?? ''
});

export const computeStockStatus = (
  quantityOnHand: number,
  minQuantity: number | null
): InventoryItemSummary['stockStatus'] => {
  if (quantityOnHand <= 0) return 'OUT_OF_STOCK';
  if (minQuantity !== null && quantityOnHand <= minQuantity) return 'LOW_STOCK';
  return 'IN_STOCK';
};

/**
 * Unit label for quantity_on_hand / min_quantity (par).
 * When conversion exists, those values are in baseUnit; otherwise purchase unit.
 */
export const getStockDisplayUnit = (
  purchaseUnit: string,
  baseUnit?: string | null
): string => {
  const base = baseUnit?.trim();
  if (base) return base;
  const purchase = purchaseUnit?.trim();
  return purchase || 'unit';
};

export const draftsToSummary = (
  items: InventoryItemDraft[]
): InventoryItemSummary[] =>
  items.map(item => {
    const qty = parseFloat(item.quantityOnHand) || 0;
    const minQty =
      item.minQuantity !== '' ? parseFloat(item.minQuantity) : null;

    return {
      id: item.localId,
      name: item.name.trim(),
      unit: item.unit,
      stockUnit: getStockDisplayUnit(item.unit, item.baseUnit),
      unitDefinition: formatUnitDefinition(
        item.unit,
        item.unitMultiplier,
        item.baseUnit
      ),
      quantityOnHand: qty,
      minQuantity: minQty,
      note: item.note.trim() || null,
      imagePreview: item.imagePreview,
      stockStatus: computeStockStatus(qty, minQty)
    };
  });
